/* walleu-sticky-atc — sticky add-to-cart bar visibility + paired quantity selector.
 *
 * <walleu-sticky-atc> replaces the vendor <product-quick-add> wrapper (theme.js
 * untouched): the bar is shown whenever the main buy buttons are NOT in the
 * viewport — scrolled past OR still below the fold (the vendor logic only
 * showed it after the whole product form scrolled above) — and hidden while
 * the footer (.footer anchor) is in view. It toggles the same .is-visible
 * class on the same .product-quick-add class, so the vendor CSS transitions
 * and the social-popup body:has() rule keep working unchanged.
 *
 * Mirrors the main product form quantity input (#<form-id>-quantity): the
 * +/- buttons and typed values here are written to it, and its current value
 * is read back whenever the bar becomes visible. The bar input never submits
 * (it has no form attribute) — only the main form input does, so quantity
 * stays a single source of truth.
 */
class WalleuStickyAtc extends HTMLElement {
  connectedCallback() {
    // Re-query on every connect: product-rerender replaces this markup (and
    // the main product info) on variant changes.
    const formId = this.getAttribute('form');
    this._buyButtons = Array.from(document.querySelectorAll(`buy-buttons[form="${formId}"]`))
      .find((element) => !element.closest('.product-quick-add'));
    this._footer = document.querySelector('.footer');

    if (!this._buyButtons) {
      return; // no main buy buttons to track — keep the bar hidden
    }

    this._buyButtonsInView = true;
    this._footerInView = false;

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this._buyButtons) {
          this._buyButtonsInView = entry.isIntersecting;
        }

        if (entry.target === this._footer) {
          this._footerInView = entry.isIntersecting;
        }
      });

      this.classList.toggle('is-visible', !this._buyButtonsInView && !this._footerInView);
    });

    this._observer.observe(this._buyButtons);

    if (this._footer) {
      this._observer.observe(this._footer);
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}

if (!window.customElements.get('walleu-sticky-atc')) {
  window.customElements.define('walleu-sticky-atc', WalleuStickyAtc);
}

class WalleuQuantityPaired extends HTMLElement {
  connectedCallback() {
    // Re-query on every connect: product-rerender replaces this markup (and
    // the main input) on variant changes, so cached references would go stale.
    this._target = document.getElementById(this.dataset.targetId);
    this._input = this.querySelector('input');

    if (!this._target || !this._input) {
      this.hidden = true;
      return;
    }

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    ['min', 'max', 'step'].forEach((attribute) => {
      if (this._target.hasAttribute(attribute)) {
        this._input.setAttribute(attribute, this._target.getAttribute(attribute));
      }
    });
    this._pull();

    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        button.matches('[data-step="-1"]') ? this._input.stepDown() : this._input.stepUp();
        this._push();
      }, { signal });
    });

    this._input.addEventListener('change', () => this._push(), { signal });
    this._target.addEventListener('change', () => this._pull(), { signal });

    // The vendor +/- buttons update the main input without dispatching events,
    // so re-read it when the bar gains visibility (class toggled by theme.js).
    const bar = this.closest('.product-quick-add');

    if (bar) {
      this._observer = new MutationObserver(() => {
        if (bar.classList.contains('is-visible')) {
          this._pull();
        }
      });
      this._observer.observe(bar, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._observer?.disconnect();
  }

  _pull() {
    this._input.value = this._target.value;
  }

  _push() {
    const value = parseInt(this._input.value) || parseInt(this._input.min) || 1;

    if ('quantity' in this._target) {
      this._target.quantity = value; // vendor QuantityInput setter clamps to quantity_rule
    } else {
      this._target.value = value;
    }

    this._pull();
  }
}

if (!window.customElements.get('walleu-quantity-paired')) {
  window.customElements.define('walleu-quantity-paired', WalleuQuantityPaired);
}
